import React, { useState, useEffect, useRef } from 'react';
import A2UIRenderer from './A2UIRenderer';

/**
 * ComponentTreeRenderer
 * Handles streaming component tree updates and renders them using A2UIRenderer
 * 
 * Supports new protocol format (add operation only):
 * - { "parent": "root", "component": { "id": "...", "type": "...", ... } }
 * No "type": "add" needed - frontend defaults to add operation
 */
const ComponentTreeRenderer = ({ chunks = [], onEvent, className, style }) => {
  const [componentTree, setComponentTree] = useState(null);
  const [widgets, setWidgets] = useState([]);
  
  // Use refs to maintain component tree and mapping
  const componentMapRef = useRef(new Map()); // ID到组件的映射表
  const componentTreeRef = useRef(null); // 保存最新的组件树引用
  const processedChunksCountRef = useRef(0); // 跟踪已处理的chunk数量
  const newlyAddedComponentsRef = useRef(new Set()); // 跟踪新添加的组件ID，用于动画
  const processingTimeoutRef = useRef(null); // 用于延迟处理chunks
  const scrollTimeoutRef = useRef(null); // 用于延迟滚动

  // build component id map
  const buildComponentMap = (node, parent = null) => {
    if (!node) return;
    
    if (node.id) {
      componentMapRef.current.set(node.id, { node, parent });
    }
    
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach(child => {
        if (typeof child === 'object' && child.id) {
          buildComponentMap(child, node);
        }
      });
    }
  };

  // find component by id
  const findComponentById = (id) => {
    return componentMapRef.current.get(id);
  };

  // add component (return new tree)
  const addComponent = (parentId, component) => {
    let currentTree = componentTreeRef.current || componentTree;
    
    // if the tree does not exist, create a default root container
    if (!currentTree) {
      console.log('🌳 tree does not exist, create a default root container');
      currentTree = { id: 'root', type: 'Container', children: [] };
      componentTreeRef.current = currentTree;
      buildComponentMap(currentTree);
    }
    
    // deep copy the whole tree
    const newTree = JSON.parse(JSON.stringify(currentTree));
    
    // find the parent component in the copied tree
    const findAndAdd = (node) => {
      if (node.id === parentId) {
        // Ensure component has an ID, generate one if missing
        if (!component.id) {
          component.id = `auto-id-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
          console.warn(`⚠️ Component missing ID, generated: ${component.id}`);
        }

        const componentId = component.id;
        const componentType = component.type;
        
        // Special handling: Row/Card components should be added to List/Carousel items array
        // instead of children array
        if ((componentType === 'Row' && node.type === 'List') || 
            (componentType === 'Card' && node.type === 'Carousel')) {
          // Add to items array
          if (!node.items) {
            node.items = [];
          }
          
          // Convert component to item object (exclude id, type, children)
          const { id, type, children, ...itemProps } = component;
          const item = {
            id: componentId,
            ...itemProps  // title, description, price, currency, image_url, etc.
          };
          
          // Check if item with same ID already exists
          const existingIndex = node.items.findIndex(item => 
            item && typeof item === 'object' && item.id === componentId
          );
          
          if (existingIndex !== -1) {
            // Item already exists, update it
            console.log(`⚠️ Item ${componentId} already exists in ${parentId} items, updating instead of adding duplicate`);
            node.items[existingIndex] = item;
          } else {
            // Item doesn't exist, add it
            node.items.push(item);
          }
          
          // Also add component to children for component map tracking
          if (!node.children) {
            node.children = [];
          }
          const existingChildIndex = node.children.findIndex(child => 
            child && typeof child === 'object' && child.id === componentId
          );
          if (existingChildIndex !== -1) {
            node.children[existingChildIndex] = component;
          } else {
            node.children.push(component);
          }
          
          return true;
        }
        
        // Normal case: add to children array
        if (!node.children) {
          node.children = [];
        }
        
        // Check if component with same ID already exists in children
        const existingIndex = node.children.findIndex(child => 
          child && typeof child === 'object' && child.id === componentId
        );
        
        if (existingIndex !== -1) {
          // Component already exists, update it instead of adding duplicate
          console.log(`⚠️ Component ${componentId} already exists in ${parentId}, updating instead of adding duplicate`);
          node.children[existingIndex] = component;
          // Also update in componentMapRef directly for immediate consistency
          componentMapRef.current.set(componentId, { node: component, parent: node });
          return true;
        }
        
        // Component doesn't exist, add it
        node.children.push(component);
        return true;
      }
      
      if (node.children) {
        for (const child of node.children) {
          if (typeof child === 'object' && child.id) {
            if (findAndAdd(child)) {
              return true;
            }
          }
        }
      }
      return false;
    };
    
    if (findAndAdd(newTree)) {
      // rebuild the component map
      componentMapRef.current.clear();
      buildComponentMap(newTree);
      return newTree;
    }
    
    console.warn(`⚠️ parent component ${parentId} not found, current tree:`, newTree);
    return null;
  };

  // Auto-scroll to bottom when components are added
  const scrollToBottom = () => {
    // Find the chat messages container
    const messagesContainer = document.querySelector('.chat-messages');
    if (messagesContainer) {
      // Use requestAnimationFrame to ensure DOM is fully updated
      requestAnimationFrame(() => {
        // Smooth scroll to bottom
        messagesContainer.scrollTo({
          top: messagesContainer.scrollHeight,
          behavior: 'smooth'
        });
      });
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Process streaming chunks incrementally
  useEffect(() => {
    // console.log('🔍 ComponentTreeRenderer useEffect triggered, chunks:', chunks, 'chunks.length:', chunks?.length);
    
    if (!chunks || chunks.length === 0) {
      console.log('⚠️ No chunks, resetting state');
      // Reset when chunks are cleared
      componentMapRef.current.clear();
      componentTreeRef.current = null;
      processedChunksCountRef.current = 0;
      newlyAddedComponentsRef.current.clear();
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      setComponentTree(null);
      setWidgets([]);
      return;
    }

    // Only process new chunks (incremental processing)
    const processedCount = processedChunksCountRef.current;
    
    if (chunks.length <= processedCount) {
      console.debug('🔄 Chunks array updated but no new chunks, skipping');
      return;
    }

    const newChunks = chunks.slice(processedCount);

    console.log('📊 Processing chunks - total:', chunks.length, 'processed:', processedCount, 'new:', newChunks.length);
    
    if (newChunks.length === 0) {
      console.log('ℹ️ No new chunks to process');
      return; // No new chunks to process
    }

    // Process chunks incrementally with animation
    // Clear any existing timeout
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
    }

    // Process chunks one by one with delay for animation effect
    const processChunk = (index) => {
      if (index >= newChunks.length) {
        // All chunks processed
        processedChunksCountRef.current = chunks.length;
        // Clear newly added components after animation completes
        setTimeout(() => {
          newlyAddedComponentsRef.current.clear();
        }, 500);
        return;
      }

      const chunk = newChunks[index];
      if (!chunk || typeof chunk !== 'object') {
        // Skip invalid chunk and process next
        processChunk(index + 1);
        return;
      }

      try {
        // New protocol format: {"parent": "root", "component": {...}}
        // Default to 'add' operation if chunk has parent and component (no type field needed)
        let chunkType = chunk.type;
        if (!chunkType && chunk.parent && chunk.component) {
          chunkType = 'add';  // Default operation is add
        }
        chunkType = chunkType || 'unknown';

        if (chunkType === 'add') {
          // add component (default operation)
          // Format: {"parent": "root", "component": {...}}
          // No "type": "add" needed - frontend defaults to add operation
          if (chunk.component && chunk.parent) {
            const componentId = chunk.component.id;
            
            // Check if component already exists in the tree (prevent duplicate additions)
            // This is the primary check - if component exists, addComponent will update it
            // But we can also check here to avoid unnecessary processing
            if (componentId && componentMapRef.current.has(componentId)) {
              const existing = componentMapRef.current.get(componentId);
              // Check if it's in the same parent (if parent matches, it's likely a duplicate)
              if (existing && existing.parent && existing.parent.id === chunk.parent) {
                console.warn(`⚠️ Component ${componentId} already exists in ${chunk.parent}, skipping duplicate add chunk`);
                // Still call addComponent to update it, but skip animation
                const newTree = addComponent(chunk.parent, chunk.component);
                if (newTree) {
                  componentTreeRef.current = newTree;
                  setComponentTree(newTree);
                }
                processChunk(index + 1);
                return;
              }
            }
            
            // mark as newly added component, for animation
            if (componentId) {
              newlyAddedComponentsRef.current.add(componentId);
            }
            
            // ensure the component tree exists
            if (!componentTreeRef.current && !componentTree) {
              console.log('🌳 receive add chunk but tree does not exist, create root');
              const defaultTree = { id: 'root', type: 'Container', children: [] };
              componentTreeRef.current = defaultTree;
              buildComponentMap(defaultTree);
              setComponentTree(defaultTree);
            }
            
            const newTree = addComponent(chunk.parent, chunk.component);
            if (newTree) {
              // console.log('✅ added tree:', newTree);
              componentTreeRef.current = newTree;
              setComponentTree(newTree);
              
              // Auto-scroll after component is added (with delay to allow DOM update)
              // Clear any pending scroll
              if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
              }
              // Use double requestAnimationFrame to ensure DOM is fully updated
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  scrollToBottom();
                });
              });
              
              // Process next chunk with delay for animation effect (150ms for smooth animation)
              processingTimeoutRef.current = setTimeout(() => processChunk(index + 1), 150);
            } else {
              console.warn('❌ add failed, return null');
              // Process next chunk even if this one failed
              processingTimeoutRef.current = setTimeout(() => processChunk(index + 1), 50);
            }
          } else {
            // Process next chunk if chunk is invalid
            processingTimeoutRef.current = setTimeout(() => processChunk(index + 1), 50);
          }
        } else {
          // Unknown or unsupported chunk type, log but do not process
          console.warn('⚠️ receive unsupported chunk type, ignored:', chunkType, chunk);
          // Process next chunk
          processingTimeoutRef.current = setTimeout(() => processChunk(index + 1), 50);
        }
      } catch (err) {
        console.warn('⚠️ process chunk failed:', chunk, err);
        // Process next chunk even on error
        processingTimeoutRef.current = setTimeout(() => processChunk(index + 1), 50);
      }
    };

    // Start processing from first new chunk
    if (newChunks.length > 0) {
      processChunk(0);
    } else {
      processedChunksCountRef.current = chunks.length;
    }
  }, [chunks]);

  // Update widgets when component tree changes
  useEffect(() => {
    // console.log('🔄 componentTree changed:', componentTree);
    if (componentTree) {
      const extractedWidgets = extractWidgetsFromTree(componentTree);
      // console.log('📦 Extracted widgets from tree:', extractedWidgets, 'count:', extractedWidgets.length);
      setWidgets(extractedWidgets);
      
      // Auto-scroll when widgets are updated
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToBottom();
        });
      });
    } else {
      console.log('⚠️ componentTree is null, clearing widgets');
      setWidgets([]);
    }
  }, [componentTree]);

  // Extract widgets from component tree for A2UIRenderer
  const extractWidgetsFromTree = (tree, isNewlyAdded = false) => {
    if (!tree) return [];
    
    // Validate tree has required properties
    if (!tree.id) {
      console.warn('⚠️ component missing id attribute:', tree);
      return [];
    }

    // Check if this component is newly added
    const componentIsNew = newlyAddedComponentsRef.current.has(tree.id) || isNewlyAdded;

    // Map component types to widget types
    const typeMapping = {
      'Text': 'text',
      'Button': 'button',
      'List': 'list',
      'Card': 'card',
      'Row': 'list',  // Row is used as list items, render as list item
      'Carousel': 'carousel',
      'Form': 'form',
      'Input': 'input',
      'Select': 'select',
      'Checkbox': 'checkbox',
      'Date': 'date',
      'DateInput': 'date',
      'Container': 'container'
    };

    // Safely get widget type
    let widgetType = 'container'; // default type
    if (tree.type) {
      widgetType = typeMapping[tree.type] || (typeof tree.type === 'string' ? tree.type.toLowerCase() : 'container');
    }
    
    // Convert component to widget format
    // According to the new protocol, component properties are directly on the component object,
    // not nested in a props object. Pass all properties directly (excluding id, type, children).
    const { id, type, children, ...componentProps } = tree;
    
    // Merge with tree.props if it exists (for backward compatibility)
    const allProps = {
      ...componentProps,
      ...(tree.props || {}),
      // Add animation class for newly added components
      className: componentIsNew 
        ? `${(componentProps.className || tree.props?.className || '')} component-newly-added`.trim()
        : (componentProps.className || tree.props?.className)
    };
    
    // Use flat format: properties directly on widget object, no props nesting
    const widget = {
      id: tree.id,
      type: widgetType,
      ...allProps  // Spread all properties directly onto widget
    };

    // Handle items array (for List/Carousel - items are populated by addComponent)
    // Items are already converted from Row/Card components in addComponent
    if (tree.items && Array.isArray(tree.items)) {
      widget.items = tree.items;
    }
    
    // Handle children - could be array of objects or array of IDs
    if (tree.children && Array.isArray(tree.children) && tree.children.length > 0) {
      // Check if children are objects or IDs
      if (typeof tree.children[0] === 'object' && tree.children[0].id) {
        // For List/Carousel, Row/Card children are already converted to items in addComponent
        // So we filter them out from children to avoid duplicate rendering
        if (tree.type === 'List' || tree.type === 'Carousel') {
          // Filter out Row/Card children (they're already in items array)
          widget.children = tree.children
            .filter(child => {
              if (!child || typeof child !== 'object' || !child.id) return false;
              // For List, filter out Row children
              if (tree.type === 'List' && child.type === 'Row') return false;
              // For Carousel, filter out Card children
              if (tree.type === 'Carousel' && child.type === 'Card') return false;
              return true;
            })
            .flatMap(child => {
              if (child && typeof child === 'object' && child.id) {
                return extractWidgetsFromTree(child, false);
              }
              return [];
            });
        } else {
          // For other components, children are component objects
          widget.children = tree.children.flatMap(child => {
            if (child && typeof child === 'object' && child.id) {
              return extractWidgetsFromTree(child, false);
            }
            return [];
          });
        }
      } else {
        // Children might be IDs (legacy format), skip for now
        widget.children = [];
      }
    }

    // If it's a container, return its children as widgets (flatten)
    if (tree.type === 'Container' && tree.children && tree.children.length > 0) {
      const childWidgets = [];
      tree.children.forEach(child => {
        if (typeof child === 'object' && child.id) {
          const childWidgetsResult = extractWidgetsFromTree(child, false);
          if (childWidgetsResult && childWidgetsResult.length > 0) {
            childWidgets.push(...childWidgetsResult);
          }
        }
      });
      return childWidgets.length > 0 ? childWidgets : [widget];
    }

    return [widget];
  };
  
  if (!widgets || widgets.length === 0) {
    console.warn('⚠️ No widgets to render! componentTree:', componentTree, 'widgets:', widgets);
    return null;
  }

  return (
    <div className={`component-tree-renderer ${className || ''}`} style={style}>
      <A2UIRenderer 
        widgets={widgets} 
        onEvent={onEvent}
        className="component-tree-widgets"
      />
    </div>
  );
};

export default ComponentTreeRenderer;

